import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UploadedFileEntry } from '@interfaces/mfe/mfe-document-upload.interface';
import {
  FetchDocumentsResponse,
  FileDescriptor,
  SubmitDocumentsResponse,
  UploadDocumentsResponse,
} from '@interfaces/models/doc-types';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import { WindowRef } from '@shared/modules/window-ref/window-ref';
import {
  areBlobUrlsValid,
  getFileDescriptorsFromMetadata,
} from '@src/app/secure/document-upload/helpers';
import { IFileMetaData } from '@store/reducers/document-upload/document-upload.reducer';
import { firstValueFrom } from 'rxjs';

const MIME_TYPE_MAP: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

export function normalizeWldMimeType(mimeType: string | null | undefined): string {
  if (!mimeType) {
    return 'application/octet-stream';
  }

  const lower = mimeType.toLowerCase().trim();
  // Take the part after "/" when a full MIME type is provided (e.g. "image/jpg" -> "jpg").
  const key = lower.substring(lower.indexOf('/') + 1);

  return MIME_TYPE_MAP[key] ?? 'application/octet-stream';
}

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  constructor(
    private http: HttpClient,
    private windowRef: WindowRef,
    private loggerService: LoggerService,
  ) {}

  async submitDocuments(
    filesMetaData: IFileMetaData[],
    shouldRehydrate = false,
  ): Promise<SubmitDocumentsResponse | undefined> {
    const formData = new FormData();

    if (filesMetaData.length > 0) {
      const fileDescriptors = shouldRehydrate ? getFileDescriptorsFromMetadata(filesMetaData) : [];
      const files = fileDescriptors.length
        ? await this.rehydrateFiles(filesMetaData, fileDescriptors)
        : filesMetaData;

      for (const file of files) {
        const fetchResponse = await this.windowRef.nativeWindow.fetch(file.objectURL);
        const fetchResponseBlob = await fetchResponse.blob();
        formData.append('files', fetchResponseBlob, file.fileName);
      }

      return firstValueFrom(
        this.http.post<SubmitDocumentsResponse>('secure/documents/submit', formData),
      );
    }
  }

  async submitWldDocuments(
    wldDocuments: UploadedFileEntry[],
  ): Promise<SubmitDocumentsResponse | undefined> {
    if (wldDocuments.length > 0) {
      const documents = wldDocuments.map((document) => ({
        ...document,
        mimeType: normalizeWldMimeType(document.mimeType),
      }));

      return firstValueFrom(
        this.http.post<SubmitDocumentsResponse>('secure/documents/submit', {
          documents,
        }),
      );
    }
  }

  async fetchDocuments(fileDescriptors?: FileDescriptor[]): Promise<IFileMetaData[] | undefined> {
    if (!fileDescriptors || fileDescriptors.length === 0) {
      return undefined;
    }

    const response = await firstValueFrom(
      this.http.post<FetchDocumentsResponse>('secure/documents/fetch', { fileDescriptors }),
    );

    return response.fetchedFiles.map(({ filename, fileExtension, file }) => {
      const fileName = `${filename}.${fileExtension.toLowerCase()}`;
      const mimeType = MIME_TYPE_MAP[fileExtension.toLowerCase()] ?? 'application/octet-stream';
      const bytes = new Uint8Array(Object.values(file));
      const blob = new Blob([bytes], { type: mimeType });
      const objectURL = this.windowRef.nativeWindow.URL.createObjectURL(blob);
      return { fileName, objectURL };
    });
  }

  async rehydrateFiles(
    filesMetaData: IFileMetaData[],
    fileDescriptors: FileDescriptor[],
  ): Promise<IFileMetaData[]> {
    if (filesMetaData.length === 0) {
      return filesMetaData;
    }

    const hasValidBlobs = await areBlobUrlsValid(filesMetaData, this.windowRef);
    if (hasValidBlobs) {
      return filesMetaData;
    }

    this.loggerService.info(
      {
        eventId: 'submit-rehydrate-blobs-invalid',
        message: `Blob URLs invalid for ${filesMetaData.length} file(s)`,
      },
      LoggerCodes.GENERAL_SUCCESS,
    );

    if (fileDescriptors.length > 0) {
      const freshFiles = await this.fetchDocuments(fileDescriptors);
      return freshFiles || [];
    }

    this.loggerService.info(
      {
        eventId: 'submit-rehydrate-no-descriptors',
        message: `No file descriptors available — cannot rehydrate ${filesMetaData.length} file(s)`,
      },
      LoggerCodes.NULL_FAILURE,
    );
    return [];
  }

  async uploadDocuments(
    filesMetaData: IFileMetaData[],
  ): Promise<UploadDocumentsResponse | undefined> {
    const formData = new FormData();

    if (filesMetaData.length > 0) {
      for (const file of filesMetaData) {
        const fetchResponse = await this.windowRef.nativeWindow.fetch(file.objectURL);
        const fetchResponseBlob = await fetchResponse.blob();
        formData.append('files', fetchResponseBlob, file.s3BucketFileName!);
      }

      return firstValueFrom(
        this.http.post<UploadDocumentsResponse>('secure/documents/upload', formData),
      );
    }
  }

  /**
   * Errors are deliberately left to propagate to the caller (the `deleteFromS3$`
   * effect), which logs and swallows them — deletion is best-effort cleanup.
   */
  async deleteDocuments(draftId: string, fileDescriptors: FileDescriptor[]): Promise<void> {
    if (fileDescriptors.length === 0) {
      return;
    }

    await firstValueFrom(this.http.post('secure/documents/delete', { draftId, fileDescriptors }));
  }
}
